// "Copy for agent": builds a prompt for Claude Code (or any agent) working in this folder, which
// writes its designs to `.modelviewer/`, where the app picks them up. The app itself calls no AI.
import * as Dialog from '@radix-ui/react-dialog';
import { Bot, Copy } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { cn } from '../lib/cn';
import { toast } from '../state/toast';
import { findSet, refKey, useWorkspace, type ModelRef } from '../state/workspace';
import { buildAgentPrompt, type AgentTask, type PromptInput } from './prompt';

const useCopyDialog = create<{ open: boolean; base: ModelRef | null; noteId: string | null }>(() => ({ open: false, base: null, noteId: null }));

/** Where the agent kit lives, remembered per browser. */
const useKitPlace = create<{ kit: string; setKit: (kit: string) => void }>()(
  persist((set) => ({ kit: '.claude/skills/modelviewer', setKit: (kit) => set({ kit }) }), { name: 'mv-agent-kit' }),
);

export function openCopyForAgent(options: { base?: ModelRef | null; noteId?: string | null } = {}): void {
  const { activeRef, activeNote } = useWorkspace.getState();
  useCopyDialog.setState({ open: true, base: options.base ?? activeRef, noteId: options.noteId ?? activeNote });
}

const TASKS: { value: AgentTask; label: string; hint: string }[] = [
  { value: 'alternatives', label: 'Propose designs', hint: 'Several different sketches and a note comparing them' },
  { value: 'refine', label: 'Refine', hint: 'An improved copy of the starting model' },
  { value: 'review', label: 'Review', hint: 'A note on what works and what will hurt' },
];

const selectClass = 'h-8 rounded-md border border-border bg-bg px-2 text-[12.5px] outline-none focus:border-accent';

export function CopyForAgentDialog() {
  const { open, base: initialBase, noteId: initialNote } = useCopyDialog();
  const discovery = useWorkspace((s) => s.discovery);
  const sketches = useWorkspace((s) => s.sketches);
  const notes = useWorkspace((s) => s.notes);
  const { kit, setKit } = useKitPlace();
  const [task, setTask] = useState<AgentTask>('alternatives');
  const [count, setCount] = useState(3);
  const [baseKey, setBaseKey] = useState('');
  const [noteId, setNoteId] = useState('');
  const [useCase, setUseCase] = useState('');

  // The models to start from: each context's current model and migrations, and the sketches.
  const bases = useMemo(() => {
    const out: { key: string; label: string; ref: ModelRef }[] = [];
    for (const set of discovery?.sets ?? []) {
      out.push({ key: refKey({ setKey: set.key, migrationId: null }), label: `${set.contextName} · current model`, ref: { setKey: set.key, migrationId: null } });
      for (const m of [...set.migrations].reverse()) if (m.designerPath) out.push({ key: refKey({ setKey: set.key, migrationId: m.id }), label: `${set.contextName} · ${m.name}`, ref: { setKey: set.key, migrationId: m.id } });
    }
    for (const s of Object.values(sketches)) out.push({ key: `sketch:${s.id}`, label: `Sketch · ${s.doc.title}${s.origin ? ' (folder)' : ''}`, ref: { setKey: s.setKey ?? '', migrationId: null, sketchId: s.id } });
    return out;
  }, [discovery, sketches]);

  useEffect(() => {
    if (!open) return;
    const start = initialBase ? refKey(initialBase) : '';
    setBaseKey(bases.some((b) => b.key === start) ? start : (bases[0]?.key ?? ''));
    setNoteId(initialNote && notes[initialNote] ? initialNote : '');
    setUseCase(initialNote && notes[initialNote] ? notes[initialNote]!.body : '');
    setTask(initialBase?.sketchId ? 'refine' : 'alternatives');
    // Only when the dialog opens.
  }, [open]);

  const prompt = useMemo(() => {
    const ref = bases.find((b) => b.key === baseKey)?.ref;
    if (!ref) return '';
    const note = noteId ? notes[noteId] : undefined;
    let base: PromptInput['base'];
    if (ref.sketchId) {
      const s = sketches[ref.sketchId]!;
      base = { kind: 'sketch', title: s.doc.title, context: s.doc.basedOn?.context ?? (s.setKey ? (findSet(s.setKey)?.contextName ?? null) : null), path: s.origin?.path, doc: s.doc };
    } else {
      const set = findSet(ref.setKey);
      base = { kind: 'model', context: set?.contextName ?? '', migration: ref.migrationId ? (set?.migrations.find((m) => m.id === ref.migrationId)?.name ?? ref.migrationId) : null };
    }
    return buildAgentPrompt({ task, count, useCase, useCaseNote: note ? { title: note.title, path: note.origin?.path } : undefined, base, kit });
  }, [bases, baseKey, noteId, notes, sketches, task, count, useCase, kit]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(prompt);
      useCopyDialog.setState({ open: false });
      toast('Copied. Paste it into Claude Code (or another agent) running in this folder; its designs appear here as they are written.');
    } catch {
      toast('The browser did not allow copying. Select the text and copy it.', 'error');
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={(o) => useCopyDialog.setState({ open: o })}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/30" />
        <Dialog.Content className="fixed top-[6vh] left-1/2 z-50 flex max-h-[88vh] w-[min(760px,94vw)] -translate-x-1/2 flex-col gap-3 overflow-y-auto rounded-xl border border-border-strong bg-panel-2 p-4 shadow-[var(--shadow-pop)]">
          <Dialog.Title className="flex items-center gap-2 text-[14px] font-semibold">
            <Bot size={16} className="text-accent" /> Copy for agent
          </Dialog.Title>
          <Dialog.Description className="text-[12.5px] text-muted">
            A prompt for Claude Code, or any agent working in this folder with the modelviewer agent kit. It writes sketches and notes to <code className="font-mono">.modelviewer/</code>, and they appear here.
          </Dialog.Description>

          <div className="flex gap-1.5" role="radiogroup" aria-label="Task">
            {TASKS.map((t) => (
              <button
                key={t.value}
                role="radio"
                aria-checked={task === t.value}
                onClick={() => setTask(t.value)}
                title={t.hint}
                className={cn('flex-1 rounded-md border px-2 py-1.5 text-left text-[12.5px]', task === t.value ? 'border-accent bg-accent-soft' : 'border-border hover:bg-hover')}
              >
                <span className="font-medium">{t.label}</span>
                <span className="block text-[11.5px] text-muted">{t.hint}</span>
              </button>
            ))}
          </div>

          <div className="grid grid-cols-[110px_1fr] items-center gap-x-3 gap-y-2 text-[12.5px]">
            <label htmlFor="agent-base" className="text-muted">
              Start from
            </label>
            <select id="agent-base" value={baseKey} onChange={(e) => setBaseKey(e.target.value)} className={cn(selectClass, 'w-full')}>
              {bases.map((b) => (
                <option key={b.key} value={b.key}>
                  {b.label}
                </option>
              ))}
            </select>
            {task === 'alternatives' && (
              <>
                <label htmlFor="agent-count" className="text-muted">
                  Designs
                </label>
                <select id="agent-count" value={count} onChange={(e) => setCount(Number(e.target.value))} className={cn(selectClass, 'w-24')}>
                  {[2, 3, 4].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </>
            )}
            <label htmlFor="agent-note" className="text-muted">
              Use case note
            </label>
            <select
              id="agent-note"
              value={noteId}
              onChange={(e) => {
                setNoteId(e.target.value);
                if (e.target.value) setUseCase(notes[e.target.value]?.body ?? '');
              }}
              className={cn(selectClass, 'w-full')}
            >
              <option value="">None, describe it below</option>
              {Object.values(notes).map((n) => (
                <option key={n.id} value={n.id}>
                  {n.title}
                  {n.origin ? ' (folder)' : ''}
                </option>
              ))}
            </select>
            <label htmlFor="agent-kit" className="text-muted">
              Agent kit in
            </label>
            <select id="agent-kit" value={kit} onChange={(e) => setKit(e.target.value)} className={cn(selectClass, 'w-full')}>
              <option value=".claude/skills/modelviewer">This repository (.claude/skills/modelviewer)</option>
              <option value="~/.claude/skills/modelviewer">My home folder (~/.claude/skills/modelviewer)</option>
            </select>
          </div>
          {!(noteId && notes[noteId]?.origin) && (
            <textarea
              value={useCase}
              onChange={(e) => setUseCase(e.target.value)}
              aria-label="Use case"
              placeholder="What is the design for? Who reads and writes what, how often, and what must never happen."
              rows={4}
              className="w-full resize-y rounded-md border border-border bg-bg p-2 text-[12.5px] outline-none focus:border-accent"
            />
          )}
          <textarea readOnly value={prompt} aria-label="Prompt" rows={12} className="w-full resize-y rounded-md border border-border bg-panel p-2 font-mono text-[11.5px] text-muted outline-none" />
          <div className="flex items-center justify-end gap-2">
            <a href="/agent-kit.zip" download className="mr-auto text-[12.5px] text-accent hover:underline">
              Download the agent kit
            </a>
            <Dialog.Close className="h-8 rounded-md border border-border px-3 text-[12.5px] hover:bg-hover">Cancel</Dialog.Close>
            <button disabled={!prompt} onClick={() => void copy()} className="flex h-8 items-center gap-1.5 rounded-md bg-accent px-3 text-[12.5px] font-medium text-accent-fg hover:bg-accent-hover disabled:opacity-40">
              <Copy size={13} /> Copy prompt
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
